import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Flag as GoalIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  Timer as TimerIcon,
  LocalFireDepartment as FireIcon,
} from '@mui/icons-material';
import { Points, TeamPoints, Achievement, PointsTransaction, User, PersonalRecord } from '../../types';
import { pointsService } from '../../services/pointsService';
import { userService } from '../../services/userService';
import { avatarService } from '../../services/avatarService';
import { relayService } from '../../services/relayService';
import { useAuth } from '../../context/AuthContext';
import { formatMinutesAsRaceTime } from '../../utils/prTimeFormat';

const formatTime = formatMinutesAsRaceTime;

interface PointsDashboardProps {
  teamId: string;
  refreshTrigger?: number;
}

const PointsDashboard: React.FC<PointsDashboardProps> = ({ teamId, refreshTrigger }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [pointsSummary, setPointsSummary] = useState<any>(null);
  const [teamPoints, setTeamPoints] = useState<TeamPoints | null>(null);
  const [leaderboard, setLeaderboard] = useState<Points[]>([]);
  const [leaderboardWithNames, setLeaderboardWithNames] = useState<any[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<PointsTransaction[]>([]);
  
  // Leaderboard filters
  const [viewMode, setViewMode] = useState<'points' | 'race' | 'current-streak' | 'longest-streak' | 'level'>('points');
  const [selectedEvent, setSelectedEvent] = useState<string>('5K');
  const [includeCoaches, setIncludeCoaches] = useState(true);
  const [raceLeaderboard, setRaceLeaderboard] = useState<any[]>([]);

  useEffect(() => {
    if (user && teamId) {
      loadData();
    }
  }, [user, teamId]);

  // Listen for refresh triggers from parent components
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0 && user && teamId) {
      console.log('Points dashboard refresh triggered, trigger value:', refreshTrigger);
      loadData();
    }
  }, [refreshTrigger]);

  const loadData = async () => {
    if (!user) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Use Promise.allSettled so one failure doesn't block others
      const [summaryResult, teamResult, leaderboardResult] = await Promise.allSettled([
        pointsService.getPointsSummary(user.id, teamId),
        pointsService.getTeamPoints(teamId),
        pointsService.getTeamLeaderboard(teamId)
      ]);
      
      // Handle summary result
      if (summaryResult.status === 'fulfilled') {
        const summary = summaryResult.value;
        setPointsSummary(summary);
        setAchievements(summary.achievements || []);
        setRecentTransactions(summary.recentTransactions || []);
      } else {
        console.error('Error loading points summary:', summaryResult.reason);
        setError(summaryResult.reason?.message || 'Failed to load points summary');
      }
      
      // Handle team points result
      if (teamResult.status === 'fulfilled') {
        setTeamPoints(teamResult.value);
      } else {
        console.warn('Error loading team points:', teamResult.reason);
        // Don't set error for team points failure, it's not critical
      }
      
      // Handle leaderboard result
      let leaderboardData: Points[] = [];
      if (leaderboardResult.status === 'fulfilled') {
        leaderboardData = leaderboardResult.value;
        setLeaderboard(leaderboardData);
      } else {
        console.error('Error loading leaderboard:', leaderboardResult.reason);
        // If leaderboard fails, show error but continue
        if (!error) {
          setError(leaderboardResult.reason?.message || 'Failed to load leaderboard');
        }
      }
      
      // Fetch user names and roles for leaderboard (only if we have data)
      if (leaderboardData.length > 0) {
        console.log('Fetching user names for leaderboard:', leaderboardData.length, 'members');
        try {
          const leaderboardWithNames = await Promise.allSettled(
            leaderboardData.map(async (member) => {
              try {
                const userData = await userService.getUserById(member.userId);
                console.log('User data for', member.userId, ':', userData?.displayName);
                return {
                  ...member,
                  userName: userData?.displayName || 'Unknown User',
                  userEmail: userData?.email || '',
                  userRole: userData?.role || 'athlete'
                };
              } catch (error) {
                console.warn('Failed to fetch user data for:', member.userId, error);
                return {
                  ...member,
                  userName: 'Unknown User',
                  userEmail: '',
                  userRole: 'athlete'
                };
              }
            })
          );
          
          // Filter out failed promises and extract values
          const successfulNames = leaderboardWithNames
            .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
            .map(result => result.value);
          
          console.log('Leaderboard with names:', successfulNames);
          setLeaderboardWithNames(successfulNames);
        } catch (err) {
          console.error('Error fetching user names for leaderboard:', err);
          // Fallback: use leaderboard data without names
          setLeaderboardWithNames(leaderboardData.map(member => ({
            ...member,
            userName: 'Unknown User',
            userEmail: '',
            userRole: 'athlete'
          })));
        }
      } else {
        setLeaderboardWithNames([]);
      }
      
      // Load race leaderboard if in race view mode
      if (viewMode === 'race') {
        await loadRaceLeaderboard();
      }
    } catch (err: any) {
      console.error('Error loading points data:', err);
      setError(err.message || 'Failed to load points data');
    } finally {
      setLoading(false);
    }
  };

  const loadRaceLeaderboard = async () => {
    try {
      const isRelayEvent = ['4x800', '4x400', '4x100', 'DMR'].includes(selectedEvent);

      if (isRelayEvent) {
        const relays = await relayService.getTeamRelayResults(
          teamId,
          selectedEvent as any,
          undefined
        );

        const relayWithParticipants = await Promise.all(
          relays.map(async (relay) => {
            const legIds = relay.legAthleteIds as unknown as string[];
            const users = await Promise.all(
              legIds.map((id) =>
                userService.getUserById(id).catch(() => null)
              )
            );

            const participants = users.map((u) => u?.displayName || 'Unknown Athlete');
            const roles = users.map((u) => u?.role || 'athlete');

            const anyCoach = roles.some((r) => r === 'coach' || r === 'headCoach');
            if (!includeCoaches && anyCoach) return null;

            return {
              id: relay.id,
              isRelay: true,
              participants,
              userName: participants.join(', '),
              userRole: roles[0] || 'athlete',
              time: relay.overallTimeMinutes,
              date: relay.date,
              location: relay.location,
              isVerified: relay.isVerified,
              participantRoles: roles,
            };
          })
        );

        const filtered = relayWithParticipants.filter(Boolean) as any[];
        filtered.sort((a, b) => a.time - b.time);
        setRaceLeaderboard(filtered);
        return;
      }

      // Non-relay: PR-based leaderboard
      const allPRs = await avatarService.getTeamPRs(teamId);
      const eventPRs = allPRs.filter((pr) => pr.eventType === selectedEvent);

      const prWithUserData = await Promise.all(
        eventPRs.map(async (pr) => {
          try {
            const userData = await userService.getUserById(pr.userId);
            return {
              ...pr,
              userName: userData?.displayName || 'Unknown User',
              userRole: userData?.role || 'athlete',
            };
          } catch (error) {
            console.warn('Failed to fetch user data for PR:', pr.userId, error);
            return {
              ...pr,
              userName: 'Unknown User',
              userRole: 'athlete',
            };
          }
        })
      );

      const filteredPRs = includeCoaches
        ? prWithUserData
        : prWithUserData.filter(
            (pr) => pr.userRole !== 'coach' && pr.userRole !== 'headCoach'
          );

      const sortedPRs = filteredPRs.sort((a, b) => a.time - b.time);
      setRaceLeaderboard(sortedPRs);
    } catch (err: any) {
      console.error('Error loading race leaderboard:', err);
      setError(err.message || 'Failed to load race leaderboard');
    }
  };

  // Reload race leaderboard when filters change
  useEffect(() => {
    if (viewMode === 'race' && teamId) {
      loadRaceLeaderboard();
    }
  }, [viewMode, selectedEvent, includeCoaches, teamId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    loadData();
  };


  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    if (typeof timestamp === 'string') {
      const ymdMatch = timestamp.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (ymdMatch) {
        const y = Number(ymdMatch[1]);
        const m = Number(ymdMatch[2]) - 1;
        const d = Number(ymdMatch[3]);
        const date = new Date(y, m, d);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      }
    }
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  // A streak is only considered "current" if the user has logged within the last 36 hours.
  const getEffectiveCurrentStreak = (member: any): number => {
    const streak = member?.currentStreak || 0;
    const lastLoggedDate = member?.lastLoggedDate;
    if (!streak || !lastLoggedDate) return 0;

    const lastLogged = new Date(`${lastLoggedDate}T00:00:00`);
    if (Number.isNaN(lastLogged.getTime())) return 0;

    const hoursSinceLastLog = (Date.now() - lastLogged.getTime()) / (1000 * 60 * 60);
    return hoursSinceLastLog <= 36 ? streak : 0;
  };

  const getAchievementIcon = (type: string) => {
    switch (type) {
      case 'goal_completion': return '🎯';
      case 'distance_milestone': return '🏃‍♂️';
      case 'consistency': return '⭐';
      case 'team_contribution': return '👥';
      default: return '🏆';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center', 
        mb: 3
      }}>
        <IconButton onClick={handleRefresh} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {pointsSummary && (
        <>
          {/* Experience Points (XP) - Leveling System */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <CardContent sx={{ textAlign: 'center', color: 'white' }}>
                  <TrophyIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                    Level {pointsSummary.level || 0}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                    {pointsSummary.experiencePoints || 0} XP
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Total logged days
                  </Typography>
                  <Chip 
                    label={`Rank #${pointsSummary.rank || 'N/A'}`} 
                    sx={{ 
                      mt: 1, 
                      backgroundColor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)'
                    }} 
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Wallet Points (WP) - Spendable Currency - HIDDEN FOR NOW */}
            {/* <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <CardContent sx={{ textAlign: 'center', color: 'white' }}>
                  <TrendingUpIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                    {pointsSummary.walletPoints || 0}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                    Wallet Points
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Available for spending
                  </Typography>
                  <Chip 
                    label={`Spent: ${pointsSummary.spentPoints || 0}`} 
                    sx={{ 
                      mt: 1, 
                      backgroundColor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)'
                    }} 
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid> */}

            {/* Streak Information */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                <CardContent sx={{ textAlign: 'center', color: 'white' }}>
                  <ScheduleIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                    {pointsSummary.currentStreak || 0}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                    Day Streak
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Best: {pointsSummary.longestStreak || 0} days
                  </Typography>
                  <Chip 
                    label={`${pointsSummary.streakPoints || 0} bonus points`} 
                    sx={{ 
                      mt: 1, 
                      backgroundColor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)'
                    }} 
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Wallet Points Breakdown */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <StarIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {pointsSummary.dailyWalletPoints || 0}
                  </Typography>
                  <Typography color="text.secondary">
                    Daily Logging
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Streak Bonuses - HIDDEN FOR NOW */}
            {/* <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <TrendingUpIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {pointsSummary.streakPoints || 0}
                  </Typography>
                  <Typography color="text.secondary">
                    Streak Bonuses
                  </Typography>
                </CardContent>
              </Card>
            </Grid> */}
            
            {/* Challenges - HIDDEN FOR NOW */}
            {/* <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <GoalIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {pointsSummary.challengePoints || 0}
                  </Typography>
                  <Typography color="text.secondary">
                    Challenges
                  </Typography>
                </CardContent>
              </Card>
            </Grid> */}

            {/* Missions - HIDDEN FOR NOW */}
            {/* <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <TrophyIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {pointsSummary.missionPoints || 0}
                  </Typography>
                  <Typography color="text.secondary">
                    Missions
                  </Typography>
                </CardContent>
              </Card>
            </Grid> */}
          </Grid>

          {/* Team Points */}
          {teamPoints && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PeopleIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Team Total: {teamPoints.totalPoints} points
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {teamPoints.memberCount} members
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg: {teamPoints.averagePoints.toFixed(1)} points/member
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Leaderboard" />
              {/* Achievements Tab - HIDDEN FOR NOW */}
              {/* <Tab label="Achievements" /> */}
              <Tab label="Recent Activity" />
            </Tabs>
          </Box>

          {/* Leaderboard Tab */}
          {tabValue === 0 && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">
                    Team Leaderboard
                  </Typography>
                </Box>

                {/* Filter Controls */}
                <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>View By</InputLabel>
                    <Select
                      value={viewMode}
                      label="View By"
                      onChange={(e) => setViewMode(e.target.value as 'points' | 'race' | 'current-streak' | 'longest-streak' | 'level')}
                    >
                      <MenuItem value="points">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrophyIcon fontSize="small" />
                          Points
                        </Box>
                      </MenuItem>
                      <MenuItem value="level">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUpIcon fontSize="small" />
                          Level
                        </Box>
                      </MenuItem>
                      <MenuItem value="race">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TimerIcon fontSize="small" />
                          Race Times
                        </Box>
                      </MenuItem>
                      <MenuItem value="current-streak">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FireIcon fontSize="small" />
                          Current Streaks
                        </Box>
                      </MenuItem>
                      <MenuItem value="longest-streak">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FireIcon fontSize="small" />
                          Longest Streaks
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {viewMode === 'race' && (
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Event</InputLabel>
                      <Select
                        value={selectedEvent}
                        label="Event"
                        onChange={(e) => setSelectedEvent(e.target.value)}
                      >
                        <MenuItem value="400m">400m</MenuItem>
                        <MenuItem value="800m">800m</MenuItem>
                        <MenuItem value="1600m">1600m (Mile)</MenuItem>
                        <MenuItem value="3200m">3200m (2 Mile)</MenuItem>
                        <MenuItem value="3k">3k</MenuItem>
                        <MenuItem value="5K">5K</MenuItem>
                        <MenuItem value="10K">10K</MenuItem>
                        <MenuItem value="4x800">4x800</MenuItem>
                        <MenuItem value="4x400">4x400</MenuItem>
                        <MenuItem value="4x100">4x100</MenuItem>
                        <MenuItem value="DMR">DMR</MenuItem>
                        <MenuItem value="Half Marathon">Half Marathon</MenuItem>
                        <MenuItem value="Marathon">Marathon</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeCoaches}
                        onChange={(e) => setIncludeCoaches(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Include Coaches"
                  />
                </Stack>

                {/* Points Leaderboard */}
                {viewMode === 'points' && (
                  <List>
                    {leaderboardWithNames.length > 0 ? (
                      leaderboardWithNames
                        .filter(member => includeCoaches || (member.userRole !== 'coach' && member.userRole !== 'headCoach'))
                        .map((member, index) => {
                          const filteredList = leaderboardWithNames.filter(m => includeCoaches || (m.userRole !== 'coach' && m.userRole !== 'headCoach'));
                          return (
                            <ListItem key={member.id} divider={index < filteredList.length - 1}>
                              <ListItemIcon>
                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                  {getRankIcon(index + 1)}
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>{member.userName} - {member.totalPoints || member.points || 0} points</span>
                                    {(member.userRole === 'coach' || member.userRole === 'headCoach') && (
                                      <Chip label={member.userRole === 'headCoach' ? 'Head Coach' : 'Coach'} size="small" color="secondary" sx={{ height: 20 }} />
                                    )}
                                  </Box>
                                }
                                secondary={`XP: ${member.availablePoints || 0} • Wallet: ${member.walletPoints || 0} • Level: ${member.level || 0}`}
                              />
                            </ListItem>
                          );
                        })
                    ) : (
                      leaderboard.map((member, index) => (
                        <ListItem key={member.id} divider={index < leaderboard.length - 1}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {getRankIcon(index + 1)}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={`User ${member.userId.slice(0, 8)}... - ${member.totalPoints || member.points || 0} points`}
                            secondary={`XP: ${member.availablePoints || 0} • Wallet: ${member.walletPoints || 0} • Level: ${member.level || 0}`}
                          />
                        </ListItem>
                      ))
                    )}
                  </List>
                )}

                {/* Level Leaderboard */}
                {viewMode === 'level' && (
                  <List>
                    {leaderboardWithNames.length > 0 ? (
                      leaderboardWithNames
                        .filter(member => includeCoaches || (member.userRole !== 'coach' && member.userRole !== 'headCoach'))
                        .sort((a, b) => (b.level || 0) - (a.level || 0))
                        .map((member, index) => {
                          const filteredList = leaderboardWithNames
                            .filter(m => includeCoaches || (m.userRole !== 'coach' && m.userRole !== 'headCoach'))
                            .sort((a, b) => (b.level || 0) - (a.level || 0));
                          return (
                            <ListItem key={member.id} divider={index < filteredList.length - 1}>
                              <ListItemIcon>
                                <Avatar sx={{ bgcolor: 'info.main' }}>
                                  {getRankIcon(index + 1)}
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>{member.userName} - Level {member.level || 0}</span>
                                    {(member.userRole === 'coach' || member.userRole === 'headCoach') && (
                                      <Chip label={member.userRole === 'headCoach' ? 'Head Coach' : 'Coach'} size="small" color="secondary" sx={{ height: 20 }} />
                                    )}
                                  </Box>
                                }
                                secondary={`${member.experiencePoints || 0} XP • Current Streak: ${member.currentStreak || 0} days • Total Points: ${member.totalPoints || member.points || 0}`}
                              />
                            </ListItem>
                          );
                        })
                    ) : (
                      <Box sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '2px dashed',
                        borderColor: 'grey.300'
                      }}>
                        <TrendingUpIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No levels yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Start logging daily to gain experience points and level up!
                        </Typography>
                      </Box>
                    )}
                  </List>
                )}

                {/* Race Leaderboard */}
                {viewMode === 'race' && (
                  <List>
                    {raceLeaderboard.length > 0 ? (
                      raceLeaderboard.map((pr, index) => (
                        <ListItem key={pr.id} divider={index < raceLeaderboard.length - 1}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: 'success.main' }}>
                              {getRankIcon(index + 1)}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{pr.userName} - {formatTime(pr.time)}</span>
                                    {!pr.isRelay && (pr.userRole === 'coach' || pr.userRole === 'headCoach') && (
                                  <Chip label={pr.userRole === 'headCoach' ? 'Head Coach' : 'Coach'} size="small" color="secondary" sx={{ height: 20 }} />
                                )}
                              </Box>
                            }
                            secondary={
                              <>
                                {pr.location && `${pr.location} • `}
                                {formatDate(pr.date)}
                                {pr.isVerified && ' • ✓ Verified'}
                              </>
                            }
                          />
                        </ListItem>
                      ))
                    ) : (
                      <Box sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '2px dashed',
                        borderColor: 'grey.300'
                      }}>
                        <TimerIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No race times recorded
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Record your first {selectedEvent} time to appear on the leaderboard!
                        </Typography>
                      </Box>
                    )}
                  </List>
                )}

                {/* Current Streak Leaderboard */}
                {viewMode === 'current-streak' && (
                  <List>
                    {leaderboardWithNames.length > 0 ? (
                      leaderboardWithNames
                        .filter(member => includeCoaches || (member.userRole !== 'coach' && member.userRole !== 'headCoach'))
                        .filter(member => getEffectiveCurrentStreak(member) > 0)
                        .sort((a, b) => getEffectiveCurrentStreak(b) - getEffectiveCurrentStreak(a))
                        .map((member, index) => {
                          const filteredList = leaderboardWithNames
                            .filter(m => includeCoaches || (m.userRole !== 'coach' && m.userRole !== 'headCoach'))
                            .filter(m => getEffectiveCurrentStreak(m) > 0)
                            .sort((a, b) => getEffectiveCurrentStreak(b) - getEffectiveCurrentStreak(a));
                          return (
                            <ListItem key={member.id} divider={index < filteredList.length - 1}>
                              <ListItemIcon>
                                <Avatar sx={{ bgcolor: 'warning.main' }}>
                                  {getRankIcon(index + 1)}
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>{member.userName} - {getEffectiveCurrentStreak(member)} days 🔥</span>
                                    {(member.userRole === 'coach' || member.userRole === 'headCoach') && (
                                      <Chip label={member.userRole === 'headCoach' ? 'Head Coach' : 'Coach'} size="small" color="secondary" sx={{ height: 20 }} />
                                    )}
                                  </Box>
                                }
                                secondary={`Longest Ever: ${member.longestStreak || 0} days • Level: ${member.level || 0}`}
                              />
                            </ListItem>
                          );
                        })
                    ) : (
                      <Box sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '2px dashed',
                        borderColor: 'grey.300'
                      }}>
                        <FireIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No active streaks
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Start logging daily to build your streak!
                        </Typography>
                      </Box>
                    )}
                  </List>
                )}

                {/* Longest Streak Ever Leaderboard */}
                {viewMode === 'longest-streak' && (
                  <List>
                    {leaderboardWithNames.length > 0 ? (
                      leaderboardWithNames
                        .filter(member => includeCoaches || (member.userRole !== 'coach' && member.userRole !== 'headCoach'))
                        .sort((a, b) => (b.longestStreak || 0) - (a.longestStreak || 0))
                        .map((member, index) => {
                          const filteredList = leaderboardWithNames
                            .filter(m => includeCoaches || (m.userRole !== 'coach' && m.userRole !== 'headCoach'))
                            .sort((a, b) => (b.longestStreak || 0) - (a.longestStreak || 0));
                          return (
                            <ListItem key={member.id} divider={index < filteredList.length - 1}>
                              <ListItemIcon>
                                <Avatar sx={{ bgcolor: 'error.main' }}>
                                  {getRankIcon(index + 1)}
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>{member.userName} - {member.longestStreak || 0} days 🏆</span>
                                    {(member.userRole === 'coach' || member.userRole === 'headCoach') && (
                                      <Chip label={member.userRole === 'headCoach' ? 'Head Coach' : 'Coach'} size="small" color="secondary" sx={{ height: 20 }} />
                                    )}
                                  </Box>
                                }
                                secondary={`Current Streak: ${member.currentStreak || 0} days • Level: ${member.level || 0}`}
                              />
                            </ListItem>
                          );
                        })
                    ) : (
                      <Box sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '2px dashed',
                        borderColor: 'grey.300'
                      }}>
                        <FireIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No streaks recorded
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Start logging daily to build your streak!
                        </Typography>
                      </Box>
                    )}
                  </List>
                )}
              </CardContent>
            </Card>
          )}

          {/* Achievements Tab - HIDDEN FOR NOW */}
          {/* {tabValue === 1 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Your Achievements
                </Typography>
                {achievements.length === 0 ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 4,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    border: '2px dashed',
                    borderColor: 'grey.300'
                  }}>
                    <TrophyIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No achievements yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Complete goals to unlock achievements!
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {achievements.map((achievement) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={achievement.id}>
                        <Paper sx={{ p: 2, height: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h4" sx={{ mr: 1 }}>
                              {achievement.icon || getAchievementIcon(achievement.type)}
                            </Typography>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {achievement.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                +{achievement.pointsAwarded} points
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {achievement.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(achievement.unlockedAt)}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          )} */}

          {/* Recent Activity Tab */}
          {tabValue === 1 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Activity
                </Typography>
                {recentTransactions.length === 0 ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 4,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    border: '2px dashed',
                    borderColor: 'grey.300'
                  }}>
                    <ScheduleIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No recent activity
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Complete activities to see your progress here!
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {recentTransactions.map((transaction, index) => (
                      <ListItem key={transaction.id} divider={index < recentTransactions.length - 1}>
                        <ListItemIcon>
                          <Avatar sx={{ 
                            bgcolor: transaction.points > 0 ? 'success.main' : 'error.main',
                            width: 32,
                            height: 32
                          }}>
                            {transaction.points > 0 ? '+' : ''}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={transaction.description}
                          secondary={formatDate(transaction.createdAt)}
                        />
                        <Typography 
                          variant="body1" 
                          color={transaction.points > 0 ? 'success.main' : 'error.main'}
                          fontWeight="bold"
                        >
                          {transaction.points > 0 ? '+' : ''}{transaction.points}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default PointsDashboard;
